import { Button } from "@mui/material";
import { ReactNode, CSSProperties } from "react";
import { useTranslation } from "react-i18next";

/** Empty state configuration: icon, title, description, and optional action button */
export interface IEmptyStateConfig {
  /** Main title (e.g. "Empty state title") */
  title: string;
  /** Optional longer message below the title */
  description?: string;
  /** Label for the primary action button (text-style button) */
  actionLabel?: string;
  /** Called when the action button is clicked */
  onAction?: () => void;
  /** Optional custom icon; defaults to Database Help icon */
  icon?: ReactNode;
}

export const DEFAULT_EMPTY_STATE: IEmptyStateConfig = {
  title: "general.noResultsFound",
};

const DefaultEmptyStateIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.4811 3.35738C14.1439 3.50553 10.5022 4.19487 8.47198 5.25156C7.38657 5.81694 6.77735 6.51082 6.68362 7.28633C6.67002 7.39366 6.66699 10.6091 6.67002 20.083L6.67455 32.7361L6.70781 32.857C6.91794 33.631 7.60123 34.2281 8.92398 34.7935C11.1628 35.7519 15.2188 36.5002 18.9497 36.6454C19.5256 36.668 21.0102 36.6544 21.5423 36.6227C22.0457 36.5925 22.4402 36.5652 22.4463 36.5592C22.4493 36.5577 22.3843 36.4761 22.3012 36.3763C22.0079 36.0271 21.6345 35.498 21.4138 35.12L21.3231 34.9659L21.0132 34.9764C20.537 34.9946 19.0359 34.9885 18.6549 34.9689C18.4674 34.9583 18.1213 34.9356 17.8839 34.919C14.8544 34.7073 11.6677 34.0906 9.8038 33.3559C9.49239 33.2334 8.92852 32.9583 8.75618 32.8434C8.58083 32.727 8.42663 32.6 8.37372 32.529L8.32988 32.467V29.3317C8.32988 27.2077 8.33442 26.1979 8.345 26.1979C8.35407 26.1979 8.41454 26.2251 8.47954 26.2569C10.3964 27.2213 14.4055 28.0528 18.2089 28.2765C19.1296 28.3294 20.1318 28.3476 20.1424 28.3113C20.1455 28.3007 20.1666 28.1934 20.1893 28.0724C20.2467 27.7656 20.3616 27.312 20.4659 26.984C20.5143 26.8298 20.5581 26.6953 20.5612 26.6832C20.5657 26.6665 20.4841 26.6635 20.0532 26.665C17.5634 26.6786 14.2528 26.269 11.7993 25.6461C10.7804 25.3876 9.88695 25.0838 9.26866 24.7905C8.86654 24.5985 8.49768 24.3536 8.37977 24.2009L8.32988 24.1374V20.9961C8.32988 19.2667 8.33593 17.8532 8.33593 17.8532C8.34046 17.8532 8.36616 17.8668 8.39337 17.8819C8.47954 17.9333 8.96178 18.1465 9.21423 18.2463C11.441 19.1246 15.2037 19.8079 18.7078 19.9696C21.0872 20.08 23.9368 19.8563 26.7078 19.3423C28.6458 18.981 30.2543 18.5154 31.4032 17.9817L31.6708 17.8563V20.1359L31.7055 20.1435C31.7237 20.1465 31.8446 20.1707 31.9731 20.1964C32.2996 20.2614 32.6972 20.3627 33.0132 20.4624C33.1598 20.5093 33.2928 20.5516 33.308 20.5547C33.3321 20.5622 33.3337 20.2417 33.3337 13.9742C33.3337 8.33093 33.3306 7.36343 33.3125 7.24098C33.1492 6.15859 32.02 5.27726 29.9247 4.59548C28.5899 4.16162 26.8998 3.82299 24.9965 3.60682C24.0109 3.49495 23.1976 3.43297 21.9051 3.36948C21.4773 3.34831 18.986 3.33924 18.4811 3.35738ZM21.3533 5.01876C24.9784 5.13516 28.1016 5.63705 30.0729 6.41861C30.7532 6.68769 31.2369 6.96887 31.5015 7.24703C31.6224 7.37401 31.6617 7.44053 31.6496 7.49041C31.636 7.54484 31.4606 7.69752 31.2611 7.82904C29.9429 8.7013 26.4221 9.54484 22.7441 9.87137C20.4947 10.0694 18.537 10.0361 16.153 9.75648C13.1643 9.40727 10.4493 8.74514 9.08574 8.03161C8.73351 7.84869 8.37372 7.58263 8.35104 7.4889C8.33744 7.43297 8.40395 7.33622 8.55361 7.19412C8.95271 6.81317 9.75996 6.42919 10.8499 6.09812C12.8363 5.49797 15.5831 5.114 18.5869 5.02027C19.1371 5.00213 20.8257 5.00213 21.3533 5.01876ZM8.49768 9.59926C9.95044 10.3294 12.7471 11.0203 15.7146 11.3816C18.8182 11.761 21.2898 11.7565 24.407 11.3665C27.3321 11.0006 30.0699 10.3188 31.503 9.59926C31.5876 9.55844 31.6587 9.52367 31.6632 9.52367C31.6677 9.52367 31.6708 10.9402 31.6708 12.6711V15.8185L31.5544 15.9333C31.1613 16.3188 30.1696 16.7603 28.8605 17.1352C26.7199 17.7459 23.8356 18.1888 21.2173 18.3067C19.0207 18.405 16.4614 18.2145 13.9081 17.761C11.2188 17.2833 9.10237 16.5758 8.44779 15.9333L8.32988 15.8185V12.6711C8.32988 10.9402 8.3329 9.52367 8.33744 9.52367C8.34197 9.52367 8.41303 9.55844 8.49768 9.59926ZM29.565 21.6779C28.3964 21.7444 27.3004 22.0331 26.2724 22.5486C24.9724 23.1987 23.8537 24.1919 23.0464 25.4118C21.8355 27.241 21.4002 29.4677 21.831 31.6401C21.9731 32.3612 22.2135 33.0581 22.5491 33.7278C23.1991 35.0278 24.1878 36.142 25.4123 36.9538C27.0616 38.0467 29.057 38.5139 31.0359 38.2674C33.444 37.9681 35.6073 36.6242 36.9542 34.5879C38.3208 32.5244 38.6927 29.9651 37.9716 27.5736C37.5816 26.275 36.8696 25.0913 35.8885 24.1117C34.475 22.6983 32.664 21.8593 30.6655 21.6945C30.3979 21.6718 29.8219 21.6628 29.565 21.6779ZM30.6352 23.3649C31.9504 23.4829 33.2173 24.0089 34.2634 24.8736C34.472 25.046 34.924 25.4934 35.0888 25.6915C35.9807 26.7572 36.5052 28.0029 36.6383 29.3725C36.6897 29.8956 36.664 30.5743 36.5748 31.111C36.3813 32.278 35.8718 33.3725 35.0888 34.3083C34.9421 34.4836 34.4841 34.9417 34.3087 35.0883C33.243 35.9802 31.9973 36.5048 30.6277 36.6378C30.1046 36.6892 29.4259 36.6635 28.8892 36.5743C27.7222 36.3808 26.6277 35.8714 25.6919 35.0883C25.5166 34.9417 25.0585 34.4836 24.9119 34.3083C24.02 33.2425 23.4954 31.9968 23.3624 30.6272C23.311 30.1042 23.3367 29.4254 23.4259 28.8888C23.5922 27.8865 24.0034 26.9114 24.6095 26.077C24.9905 25.5509 25.5513 24.99 26.0774 24.6091C27.2656 23.7459 28.6897 23.2999 30.1439 23.3362C30.2815 23.3408 30.5022 23.3528 30.6352 23.3649ZM29.7887 24.5894C29.0782 24.6484 28.4841 24.8933 27.9973 25.3256C27.5786 25.6975 27.2445 26.2448 27.119 26.7648C27.0404 27.0959 27.1205 27.3952 27.3473 27.6098C27.6814 27.9243 28.1802 27.9182 28.5052 27.5947C28.6111 27.4889 28.6897 27.3423 28.7365 27.1654C28.7773 27.0082 28.8046 26.9492 28.8968 26.8101C29.1628 26.408 29.6375 26.2055 30.1923 26.2569C30.4055 26.278 30.5294 26.3083 30.6866 26.3808C30.7879 26.4277 30.8348 26.4625 30.9285 26.5562C31.0616 26.6922 31.1341 26.8162 31.1961 27.0218C31.2309 27.1382 31.2369 27.1866 31.2384 27.3619L31.2399 27.566L31.1855 27.6794C31.0782 27.9031 30.8877 28.1042 30.4085 28.5033C29.9399 28.8933 29.7328 29.1125 29.5544 29.4027C29.3972 29.6628 29.3064 29.8956 29.2535 30.1843C29.19 30.5229 29.1704 31.0233 29.2142 31.1941C29.2913 31.4965 29.5453 31.7459 29.8371 31.8064C29.961 31.8321 30.1923 31.8185 30.3027 31.7792C30.4962 31.7081 30.6685 31.5539 30.7637 31.368C30.8303 31.2365 30.8408 31.182 30.856 30.8691C30.8635 30.7073 30.8786 30.5335 30.8892 30.4836C30.9315 30.2856 31.0449 30.142 31.3835 29.8593C32.0502 29.2999 32.28 29.0671 32.5173 28.7073C32.6836 28.4534 32.8061 28.1631 32.8726 27.8608C32.9058 27.7066 32.9195 27.2773 32.8968 27.0747C32.8439 26.6136 32.6836 26.1556 32.4448 25.7897C32.023 25.1397 31.3563 24.733 30.5234 24.6182C30.351 24.5955 29.9278 24.5789 29.7887 24.5894ZM29.7237 32.9477C29.3352 33.0324 28.9769 33.3423 28.8363 33.7187C28.5718 34.4201 28.9648 35.182 29.6919 35.3755C29.8522 35.4194 30.1273 35.4224 30.283 35.3846C31.0238 35.2032 31.4334 34.4292 31.1643 33.7187C31.0222 33.3392 30.664 33.0309 30.2694 32.9477C30.1409 32.9205 29.8507 32.9205 29.7237 32.9477Z"
      fill="#525252"
    />
  </svg>
);

export interface ITableEmptyStateProps {
  /** Empty state content (title, description, action, icon) */
  config: IEmptyStateConfig;
  /** Optional styles for the root wrapper (e.g. position: 'absolute', top: 52 for table overlay) */
  wrapperStyle?: CSSProperties;
}

export const TableEmptyState = ({
  config,
  wrapperStyle,
}: ITableEmptyStateProps) => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: 32,
        gap: 24,
        background: "var(--theme-table-container-bg, #FFF)",
        ...wrapperStyle,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          padding: 12,
          width: 64,
          height: 64,
          background: "#F0F0F0",
          borderRadius: 40,
          flex: "none",
        }}
      >
        {config.icon ?? <DefaultEmptyStateIcon />}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: 0,
          gap: 20,
          width: "100%",
          maxWidth: 616,
          flex: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: 0,
            gap: 8,
            flex: "none",
          }}
        >
          <div
            style={{
              fontFamily: "'Forma DJR UI', Lexend, sans-serif",
              fontWeight: 500,
              fontSize: 20,
              lineHeight: "24px",
              display: "flex",
              alignItems: "center",
              textAlign: "center",
              color: "#3D3D3D",
              flex: "none",
            }}
          >
            {t(config.title)}
          </div>
          {config.description && (
            <div
              style={{
                fontFamily: "'Forma DJR UI', Lexend, sans-serif",
                fontWeight: 400,
                fontSize: 16,
                lineHeight: "24px",
                display: "flex",
                alignItems: "center",
                textAlign: "center",
                color: "#636363",
                flex: "none",
                alignSelf: "stretch",
              }}
            >
              {config.description}
            </div>
          )}
        </div>
        {(config.actionLabel || config.onAction) && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              padding: 0,
              gap: 8,
              flex: "none",
            }}
          >
            <Button
              variant="text"
              onClick={config.onAction}
              sx={{
                padding: "8px 12px",
                minWidth: 80,
                height: 36,
                borderRadius: 8,
                fontFamily: "'Forma DJR UI', Lexend, sans-serif",
                fontWeight: 400,
                fontSize: 16,
                lineHeight: "20px",
                color: "#024AD8",
              }}
            >
              {config.actionLabel ?? "Action"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
